import { useEffect, type ReactNode } from 'react';
import { useThemeStore } from '../stores/themeStore.js';

interface ThemeProviderProps {
  readonly children: ReactNode;
}

const THEME_COLOR_META: Record<'light' | 'dark', string> = {
  dark: '#1a1d23',
  light: '#fbfbfb',
};

function applyTheme(resolved: 'light' | 'dark'): void {
  document.documentElement.dataset.theme = resolved;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    meta.setAttribute('content', THEME_COLOR_META[resolved]);
  }
}

/**
 * Applies the resolved theme to the document root and listens for
 * system theme changes when the user's preference is "system".
 *
 * The first paint of the app is already in the right theme thanks to
 * the inline FOUC script in index.html — this provider keeps the DOM
 * in sync with the Zustand store after React takes over.
 */
export function ThemeProvider({ children }: ThemeProviderProps) {
  const preference = useThemeStore((s) => s.preference);
  const setPreference = useThemeStore((s) => s.setPreference);

  useEffect(() => {
    // Apply immediately for the current value, then subscribe for changes.
    applyTheme(useThemeStore.getState().resolved);
    const unsub = useThemeStore.subscribe((state) => {
      applyTheme(state.resolved);
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (preference !== 'system') return;
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      setPreference('system');
    };
    mql.addEventListener('change', handler);
    return () => {
      mql.removeEventListener('change', handler);
    };
  }, [preference, setPreference]);

  return <>{children}</>;
}
